import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Instagram, MessageSquare } from 'lucide-react';

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const serviceLinks = [
    { name: 'Ceramic Coating', path: '/ceramic-coating-fresno' },
    { name: 'Paint Correction', path: '/paint-correction-fresno' },
    { name: 'Mobile Detailing', path: '/mobile-detailing-fresno' },
    { name: 'Interior Detailing', path: '/#services' },
    { name: 'Headlight Restoration', path: '/#services' },
  ];

  const serviceAreas = [
    'Fresno, CA',
    'Madera, CA',
    'Clovis, CA',
    'Chowchilla, CA',
    'Sanger, CA',
    'Selma, CA',
  ];

  return (
    <footer className="bg-black border-t border-white/10" data-testid="footer">
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className="inline-block mb-6">
              <img 
                src={process.env.PUBLIC_URL + '/logo.png'}
                alt="Noble Detailing - Premium Auto Detailing Fresno"
                className="h-20 w-auto"
                style={{ mixBlendMode: 'lighten' }}
              />
            </Link>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Central California's premier auto detailing service. Professional ceramic coating, paint correction, and mobile detailing in Fresno, Madera, Clovis & surrounding areas.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://instagram.com/nobledetail" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-cyan-500/20 hover:border-cyan-500/50 transition-all"
                data-testid="instagram-link"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5 text-white" />
              </a>
              <a 
                href="https://tiktok.com/@nobleDetails" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-cyan-500/20 hover:border-cyan-500/50 transition-all"
                data-testid="tiktok-link"
                aria-label="TikTok"
              >
                <TikTokIcon className="w-5 h-5 text-white" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-lg font-semibold uppercase tracking-wider text-cyan-400 mb-6">
              Our Services
            </h4>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-gray-400 hover:text-cyan-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h4 className="font-heading text-lg font-semibold uppercase tracking-wider text-cyan-400 mb-6">
              Service Areas
            </h4>
            <ul className="space-y-3">
              {serviceAreas.map((area) => (
                <li key={area} className="text-gray-400 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-cyan-500" />
                  {area}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg font-semibold uppercase tracking-wider text-cyan-400 mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:5598801818" 
                  className="flex items-center gap-3 text-gray-400 hover:text-cyan-400 transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-5 h-5 text-cyan-500" />
                  <span className="font-accent font-semibold">Call 559-880-1818</span>
                </a>
              </li>
              <li>
                <a 
                  href="sms:5598801818?&body=Hi%2C%20I%27d%20like%20to%20get%20a%20quote%20for%20detailing%20services." 
                  className="flex items-center gap-3 text-gray-400 hover:text-cyan-400 transition-colors"
                  data-testid="footer-text"
                >
                  <MessageSquare className="w-5 h-5 text-cyan-500" />
                  <span className="font-accent font-semibold">Text Us</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 text-cyan-500 mt-0.5" />
                <span>Mobile Service - We Come To You!<br />Fresno & Surrounding Areas</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400">
                <Clock className="w-5 h-5 text-cyan-500" />
                <span>Available 7 Days a Week</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {currentYear} Noble Detailing. All rights reserved.
          </p>
          <p className="text-gray-500 text-sm">
            Premium Auto Detailing in Fresno, Madera, Clovis & Chowchilla
          </p>
        </div>
      </div>
    </footer>
  );
};
